import React from "react";
import {IBookingSearchCommonPersistedFields, IBookingSearchCommonFields} from "../common/booking-search-common-fields.interface";
import {FormFields, IFormField} from "../../../../models/forms/form-field.interface";
import {
    IBookingSearchByBookingDateRequest,
    IBookingSearchPagination
} from "../../../airline-webapi/requests/booking-search.requests";
import { IBookingSearchResultRecord } from "../../../airline-webapi/responses/booking-search.response";
import { BookingSearchStrategyType } from "../booking-search.strategy.interface";
import {StandardBookingSearchStrategy} from "../common/standard-booking-search.strategy";
import {NullableString} from "../../../../types/nullable-types";
import {SearchByBookingDateComponent} from "./search-by-booking-date.component";
import {TimeSpan} from "../../../../types/time-span";
import {calendarOutline} from "ionicons/icons";

interface ISearchByBookingDateFields extends IBookingSearchCommonFields {
    startDate: Date;
    endDate: Date;
}

interface ISearchByBookingDatePersistedFields extends IBookingSearchCommonPersistedFields {
    startDate: NullableString;
    endDate: NullableString;
}

export class SearchByBookingDateStrategy extends StandardBookingSearchStrategy<ISearchByBookingDateFields, ISearchByBookingDatePersistedFields> {

    get type(): BookingSearchStrategyType {
        return BookingSearchStrategyType.ByBookingDate;
    }

    get name(): string {
        return  this.services.language.translate("By booking date");
    }

    get resultsDescription(): string {
        return this.services.language.translate("Results by booking date");
    }

    protected _getIonIconName(): NullableString {
        return calendarOutline;
    }

    protected _createFilterFields(persistedFields: ISearchByBookingDatePersistedFields | null): FormFields<ISearchByBookingDateFields> {
        return {
            startDate: this._createField<Date>({
                fieldName: () => this.services.language.translate('Booking start date'),
                defaultValue: this.services.time.tryConvertToDate(persistedFields?.startDate),
                isRequired: true,
                validate: this._validateStartDate
            }),
            endDate: this._createField<Date>({
                fieldName: () => this.services.language.translate('Booking end date'),
                defaultValue:  this.services.time.tryConvertToDate(persistedFields?.endDate),
                isRequired: false,
                validate: this._validateEndDate

            }),
            ...this._createCommonFields(persistedFields)
        }
    }

    private _validateStartDate = (field: IFormField<Date>) => {
        if (!field.value) {
            return null;
        }

        // start date should not be lower than today() - 2 years
        const twoYearsAgo =  this.services.time.addYears(this.services.time.currentDate, -2);
        if (field.value < twoYearsAgo) {
            return this.services.language.translate("Start date should not be older than 2 years from today");
        }

        return null;
    };

    private _validateEndDate = (field: IFormField<Date>)=> {

        if(!field.value || !this.fields.startDate.value) {
            return null;
        }

        if(field.value.getTime() < this.fields.startDate.value.getTime()) {
            return this.services.language.translate("End date must be after start date");
        }

        if (field.value.getTime() > Date.now()) {
             return this.services.language.translate("End date cannot be bigger than the current time");
        }

        const diff = TimeSpan.fromMilliseconds(field.value.getTime() - this.fields.startDate.value.getTime());
        if(diff.totalHours > 24) {
            return this.maxEndDateMessage;
        }

        return null;
    }

    get maxEndDateMessage(): string {
        return this.services.language.translate("End date must be within 24 hours of start date.");
    }

    renderSearchFields(): React.ReactElement {
        return (
            <SearchByBookingDateComponent strategy={this}/>
        )
    }

    async _searchBookings(sessionToken: string, pagination: IBookingSearchPagination): Promise<IBookingSearchResultRecord[]> {
        const request: IBookingSearchByBookingDateRequest = {
            dotRezToken: sessionToken,
            startDateUtc: this._formatDateUtcZuluTime(this.fields.startDate.value),
            filters: this._createCommonFilters(pagination),
        }
        if(this.fields.endDate.value) {
            request.endDateUtc = this._formatDateUtcZuluTime(this.fields.endDate.value);
        }

        const response = await this.services.airlineWebapi.agencyBookingSearchByBookingDate(request);
        return response.results;
    }

    protected _collectFieldsValuesForStorage(): ISearchByBookingDatePersistedFields {
        return {
            startDate: this._formatDateUtcZuluTime(this.fields.startDate.value) || null,
            endDate: this._formatDateUtcZuluTime(this.fields.endDate.value) || null,
            ...this._collectCommonFieldsValuesForStorage(),
        }
    }



}